import React, { useEffect, useState, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator, Dimensions, FlatList, Alert, Modal, TextInput, Platform, Share, Linking, KeyboardAvoidingView } from 'react-native';
import { Image } from 'expo-image';
import * as ImagePicker from 'expo-image-picker';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';
import { getProfile, getAuthorFeed, getConvoForMembers, getDid, follow, unfollow, updateProfile, muteActor, unmuteActor, blockActor, unblockActor, like, unlike, repost, unrepost, createPost } from '../services/bluesky';
import { uploadBlob } from '../services/bluesky';
import { Theme, useThemedStyles, useTheme } from '../theme';
import { addToFavorites, removeFromFavorites, isFavorite } from '../utils/favorites';
import Hyperlink from '../components/Hyperlink';
import VideoEmbed from '../components/VideoEmbed';
import { extractPlainText } from '../utils/text';

const { width: SCREEN_WIDTH } = Dimensions.get('window');

interface Props {
  actor: string;
  onBack: () => void;
  onSelectPost?: (post: any) => void;
  onShowUserList?: (type: 'follows' | 'followers', actor: string) => void;
  onSelectRoom?: (room: any) => void;
  onSearchHashtag?: (hashtag: string) => void;
  onSearchHandle?: (handle: string) => void;
}

const Profile: React.FC<Props> = ({ actor, onBack, onSelectPost, onShowUserList, onSelectRoom, onSearchHashtag, onSearchHandle }) => {
  const [profile, setProfile] = useState<any>(null);
  const [feed, setFeed] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [isStartingChat, setIsStartingChat] = useState(false);
  const [isFollowingAction, setIsFollowingAction] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editAvatarUri, setEditAvatarUri] = useState<string | null>(null);
  const [editBannerUri, setEditBannerUri] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  const [repostMenuVisible, setRepostMenuVisible] = useState(false);
  const [selectedPostForRepost, setSelectedPostForRepost] = useState<any>(null);
  const [quoteModalVisible, setQuoteModalVisible] = useState(false);
  const [quoteText, setQuoteText] = useState('');
  const [isQuoteSending, setIsQuoteSending] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [loadingMore, setLoadingMore] = useState(false);
  const insets = useSafeAreaInsets();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useThemedStyles(createStyles);
  const myDid = getDid();
  const isOwnProfile = myDid === actor || (profile && profile.did === myDid);

  const fetchData = useCallback(async (isRefresh = true) => {
    if (!isRefresh && (!cursor || loadingMore)) return;
    
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoadingMore(true);
    }

    try {
      if (isRefresh) {
        const [profileData, feedData] = await Promise.all([
          getProfile(actor),
          getAuthorFeed(actor),
        ]);
        setProfile(profileData);
        setFeed(feedData.feed);
        setCursor(feedData.cursor);
        
        // Check if user is favorited
        if (!isOwnProfile) {
          const favorited = await isFavorite(profileData.did);
          setIsFavorited(favorited);
        }
      } else {
        const feedData = await getAuthorFeed(actor, cursor);
        setFeed(prev => [...prev, ...feedData.feed]);
        setCursor(feedData.cursor);
      }
    } catch (error) {
      console.error('Failed to fetch profile data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
      setLoadingMore(false);
    }
  }, [actor, isOwnProfile, cursor, loadingMore]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleStartChat = async () => {
    if (!profile || !onSelectRoom) return;
    
    // DM feature is only available on native platforms
    if (Platform.OS === 'web') {
      Alert.alert(
        'DM機能について',
        'DM機能はモバイルアプリでのみ利用可能です。\n\nWeb版では以下の方法でやり取りできます：\n• @' + profile.handle + ' にメンション付き投稿\n• プロフィールからフォローして、タイムラインで交流',
        [{ text: 'OK' }]
      );
      return;
    }
    
    setIsStartingChat(true);
    try {
      console.log('Starting chat with DID:', profile.did);
      const convo = await getConvoForMembers([profile.did]);
      console.log('Got convo:', convo);
      if (!convo || !convo.members) {
        throw new Error(t('profile.talkError'));
      }
      const currentDid = getDid();
      // For self-chat, otherMember might be the same as currentDid or we just use the first member
      const otherMember = convo.members.find((m: any) => m.did !== currentDid) || convo.members[0];
      
      onSelectRoom({
        id: convo.id,
        title: otherMember?.displayName || otherMember?.handle || 'Unknown',
        type: 'convo',
        uri: convo.id,
        otherDid: otherMember?.did,
      });
    } catch (error: any) {
      console.error('Failed to start chat:', error);
      Alert.alert(
        t('common.error'), 
        'DM機能の開始に失敗しました。\n' + (error.message || '')
      );
    } finally {
      setIsStartingChat(false);
    }
  };

  const handleFollowToggle = async () => {
    if (!profile) return;
    setIsFollowingAction(true);
    try {
      if (profile.viewer?.following) {
        await unfollow(profile.viewer.following);
        setProfile((prev: any) => ({
          ...prev,
          followersCount: (prev.followersCount || 1) - 1,
          viewer: { ...prev.viewer, following: undefined }
        }));
      } else {
        const res = await follow(profile.did);
        setProfile((prev: any) => ({
          ...prev,
          followersCount: (prev.followersCount || 0) + 1,
          viewer: { ...prev.viewer, following: res.uri }
        }));
      }
    } catch (error) {
      console.error('Follow toggle failed:', error);
      Alert.alert(t('common.error'), t('profile.followError'));
    } finally {
      setIsFollowingAction(false);
    }
  };

  const handleEditProfile = () => {
    setEditDisplayName(profile?.displayName || '');
    setEditDescription(profile?.description || '');
    setEditAvatarUri(null);
    setEditBannerUri(null);
    setIsEditModalVisible(true);
  };

  const handlePickAvatar = async () => {
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      aspect: [1, 1],
      quality: 0.8,
    });

    if (!result.canceled && result.assets[0]) {
      setEditAvatarUri(result.assets[0].uri);
    }
  };

  const handlePickBanner = async () => {
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      aspect: [16, 9],
      quality: 0.8,
    });

    if (!result.canceled && result.assets[0]) {
      setEditBannerUri(result.assets[0].uri);
    }
  };

  const handleSaveProfile = async () => {
    if (!profile) return;
    setIsSaving(true);
    try {
      const updates: any = {
        displayName: editDisplayName,
        description: editDescription,
      };

      // Upload avatar if changed
      if (editAvatarUri) {
        const avatarBlob = await uploadBlob(editAvatarUri, 'image/jpeg');
        updates.avatar = avatarBlob.data?.blob || avatarBlob.blob || avatarBlob; // prefer blob payload
      }

      // Upload banner if changed
      if (editBannerUri) {
        const bannerBlob = await uploadBlob(editBannerUri, 'image/jpeg');
        updates.banner = bannerBlob.data?.blob || bannerBlob.blob || bannerBlob;
      }

      await updateProfile(updates);
      
      // Refresh profile
      await fetchData();
      
      setIsEditModalVisible(false);
      Alert.alert(t('common.info'), 'プロフィールを更新しました');
    } catch (error) {
      console.error('Failed to update profile:', error);
      Alert.alert(t('common.error'), 'プロフィールの更新に失敗しました');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleFavorite = async () => {
    if (!profile) return;
    try {
      if (isFavorited) {
        await removeFromFavorites(profile.did);
        setIsFavorited(false);
        Alert.alert(t('common.success'), t('profile.removedFromFavorites'));
      } else {
        await addToFavorites({
          did: profile.did,
          handle: profile.handle,
          displayName: profile.displayName,
          avatar: profile.avatar,
          description: profile.description,
        });
        setIsFavorited(true);
        Alert.alert(t('common.success'), t('profile.addedToFavorites'));
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      Alert.alert(t('common.error'), t('profile.favoriteError'));
    }
  };

  const handleMute = async () => {
    if (!profile) return;
    Alert.alert(
      t('profile.muteTitle'),
      t('profile.muteConfirm', { user: profile.displayName || profile.handle }),
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('common.confirm'),
          onPress: async () => {
            try {
              await muteActor(profile.did);
              Alert.alert(t('common.success'), t('profile.muted'));
              setIsMenuVisible(false);
            } catch (error) {
              console.error('Failed to mute:', error);
              Alert.alert(t('common.error'), t('profile.muteError'));
            }
          },
        },
      ]
    );
  };

  const handleBlock = async () => {
    if (!profile) return;
    Alert.alert(
      t('profile.blockTitle'),
      t('profile.blockConfirm', { user: profile.displayName || profile.handle }),
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('common.confirm'),
          style: 'destructive',
          onPress: async () => {
            try {
              await blockActor(profile.did);
              Alert.alert(t('common.success'), t('profile.blocked'));
              setIsMenuVisible(false);
              onBack(); // Go back after blocking
            } catch (error) {
              console.error('Failed to block:', error);
              Alert.alert(t('common.error'), t('profile.blockError'));
            }
          },
        },
      ]
    );
  };

  if (loading && !profile) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
      </View>
    );
  }

  const renderHeader = () => (
    <View>
      {/* Cover Image (LINE Style) */}
      <View style={styles.coverContainer}>
        {profile?.banner ? (
          <Image source={{ uri: profile.banner }} style={styles.coverImage as any} />
        ) : (
          <View style={[styles.coverImage, { backgroundColor: '#273246' }]} />
        )}
        <TouchableOpacity style={[styles.backButton, { top: insets.top + 10 }]} onPress={onBack}>
          <Ionicons name="chevron-back" size={28} color={theme.colors.primaryText} />
        </TouchableOpacity>
        {isOwnProfile ? (
          <TouchableOpacity style={[styles.editButton, { top: insets.top + 10 }]} onPress={handleEditProfile}>
            <Ionicons name="create-outline" size={24} color={theme.colors.primaryText} />
          </TouchableOpacity>
        ) : (
          <TouchableOpacity style={[styles.menuButton, { top: insets.top + 10 }]} onPress={() => setIsMenuVisible(true)}>
            <Ionicons name="ellipsis-horizontal" size={24} color={theme.colors.primaryText} />
          </TouchableOpacity>
        )}
      </View>

      {/* Profile Info (LINE + SNS Hybrid) */}
      <View style={styles.profileInfoContainer}>
        <Image 
          source={{ uri: profile?.avatar || 'https://via.placeholder.com/100' }} 
          style={styles.avatar as any} 
        />
        <Text style={styles.displayName}>{profile?.displayName || profile?.handle}</Text>
        <Text style={styles.handle}>@{profile?.handle}</Text>
        
        {profile?.description && (
          <Hyperlink 
            linkDefault={true} 
            linkStyle={{ color: theme.colors.primary, textDecorationLine: 'underline' }} 
            style={styles.description}
            onHashtagPress={onSearchHashtag}
            onHandlePress={onSearchHandle}
          >
            {profile.description}
          </Hyperlink>
        )}

        {/* Stats (SNS Style) */}
        <View style={styles.statsContainer}>
          <TouchableOpacity 
            style={styles.statItem} 
            onPress={() => onShowUserList?.('followers', actor)}
          >
            <Text style={styles.statNumber}>{profile?.followersCount || 0}</Text>
            <Text style={styles.statLabel}>{t('home.followers')}</Text>
          </TouchableOpacity>
          <TouchableOpacity 
            style={styles.statItem} 
            onPress={() => onShowUserList?.('follows', actor)}
          >
            <Text style={styles.statNumber}>{profile?.followsCount || 0}</Text>
            <Text style={styles.statLabel}>{t('profile.following')}</Text>
          </TouchableOpacity>
          <View style={styles.statItem}>
            <Text style={styles.statNumber}>{profile?.postsCount || 0}</Text>
            <Text style={styles.statLabel}>{t('profile.posts')}</Text>
          </View>
        </View>

        {/* Action Buttons (LINE Style) - Hidden for own profile */}
        {!isOwnProfile && (
          <View style={styles.actionButtons}>
            {/* DM button - only on native platforms */}
            {Platform.OS !== 'web' && (
              <TouchableOpacity 
                style={styles.actionButton} 
                onPress={handleStartChat}
                disabled={isStartingChat}
              >
                {isStartingChat ? (
                  <ActivityIndicator size="small" color="white" />
                ) : (
                  <>
                    <Ionicons name="chatbubble-outline" size={24} color={theme.colors.primaryText} />
                    <Text style={styles.actionButtonText}>{t('profile.talk')}</Text>
                  </>
                )}
              </TouchableOpacity>
            )}
            <TouchableOpacity 
              style={[
                styles.actionButton, 
                profile?.viewer?.following ? styles.unfollowButton : styles.followButton
              ]}
              onPress={handleFollowToggle}
              disabled={isFollowingAction}
            >
              {isFollowingAction ? (
                <ActivityIndicator size="small" color="white" />
              ) : (
                <>
                  <Ionicons 
                    name={profile?.viewer?.following ? "person-remove-outline" : "person-add-outline"} 
                    size={24} 
                    color={theme.colors.primaryText} 
                  />
                  <Text style={styles.actionButtonText}>
                    {profile?.viewer?.following ? t('profile.unfollow') : t('profile.follow')}
                  </Text>
                </>
              )}
            </TouchableOpacity>
          </View>
        )}
      </View>

      <View style={styles.sectionHeader}>
        <Text style={styles.sectionTitle}>{t('profile.posts')}</Text>
      </View>
    </View>
  );

  const formatPostTime = (indexedAt: string) => {
    const now = new Date();
    const postDate = new Date(indexedAt);
    const diffMs = now.getTime() - postDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('common.justNow') || 'たった今';
    if (diffMins < 60) return `${diffMins}${t('common.minutesAgo') || '分前'}`;
    if (diffHours < 24) return `${diffHours}${t('common.hoursAgo') || '時間前'}`;
    if (diffDays < 7) return `${diffDays}${t('common.daysAgo') || '日前'}`;
    
    return postDate.toLocaleDateString();
  };

  const handleLike = async (post: any, event: any) => {
    event.stopPropagation();
    
    // Guard against rapid clicks while an action is pending
    if (post.viewer?.like === 'pending') return;

    const isLiked = !!post.viewer?.like;
    const previousLikeUri = post.viewer?.like;
    const previousCount = post.likeCount || 0;

    // Optimistic UI update
    setFeed(prev => prev.map(item => {
      if (item.post.uri === post.uri) {
        return {
          ...item,
          post: {
            ...item.post,
            likeCount: Math.max(0, previousCount + (isLiked ? -1 : 1)),
            viewer: { ...item.post.viewer, like: isLiked ? undefined : 'pending' }
          }
        };
      }
      return item;
    }));

    try {
      if (isLiked) {
        await unlike(previousLikeUri);
        // Unlike success: state already updated optimistically
      } else {
        const res = await like(post.uri, post.cid);
        // Update with actual URI
        setFeed(prev => prev.map(item => {
          if (item.post.uri === post.uri) {
            return {
              ...item,
              post: {
                ...item.post,
                viewer: { ...item.post.viewer, like: res.data.uri }
              }
            };
          }
          return item;
        }));
      }
    } catch (error) {
      console.error('Like action failed:', error);
      // Revert on error
      setFeed(prev => prev.map(item => {
        if (item.post.uri === post.uri) {
          return {
            ...item,
            post: {
              ...item.post,
              likeCount: previousCount,
              viewer: { ...item.post.viewer, like: previousLikeUri }
            }
          };
        }
        return item;
      }));
      Alert.alert(t('common.error'), 'アクションに失敗しました');
    }
  };

  const handleRepost = (post: any, event: any) => {
    event.stopPropagation();
    setSelectedPostForRepost(post);
    setRepostMenuVisible(true);
  };

  const executeRepost = async (post: any) => {
    setRepostMenuVisible(false);
    // Guard against rapid clicks while an action is pending
    if (post.viewer?.repost === 'pending') return;

    const isReposted = !!post.viewer?.repost;
    const previousRepostUri = post.viewer?.repost;
    const previousCount = post.repostCount || 0;

    // Optimistic UI update
    setFeed(prev => prev.map(item => {
      if (item.post.uri === post.uri) {
        return {
          ...item,
          post: {
            ...item.post,
            repostCount: Math.max(0, previousCount + (isReposted ? -1 : 1)),
            viewer: { ...item.post.viewer, repost: isReposted ? undefined : 'pending' }
          }
        };
      }
      return item;
    }));

    try {
      if (isReposted) {
        await unrepost(previousRepostUri);
      } else {
        const res = await repost(post.uri, post.cid);
        // Update with actual URI
        setFeed(prev => prev.map(item => {
          if (item.post.uri === post.uri) {
            return {
              ...item,
              post: {
                ...item.post,
                viewer: { ...item.post.viewer, repost: res.data.uri }
              }
            };
          }
          return item;
        }));
      }
    } catch (error) {
      console.error('Repost action failed:', error);
      // Revert on error
      setFeed(prev => prev.map(item => {
        if (item.post.uri === post.uri) {
          return {
            ...item,
            post: {
              ...item.post,
              repostCount: previousCount,
              viewer: { ...item.post.viewer, repost: previousRepostUri }
            }
          };
        }
        return item;
      }));
      Alert.alert(t('common.error'), 'アクションに失敗しました');
    }
  };

  const executeQuote = (post: any) => {
    setRepostMenuVisible(false);
    setSelectedPostForRepost(post.post || post);
    setQuoteText('');
    setQuoteModalVisible(true);
  };

  const handleSendQuote = async () => {
    if (!quoteText.trim() || !selectedPostForRepost) return;
    setIsQuoteSending(true);
    try {
      await createPost({
        text: quoteText,
        quote: {
          uri: selectedPostForRepost.uri,
          cid: selectedPostForRepost.cid
        }
      });
      setQuoteText('');
      setQuoteModalVisible(false);
      Alert.alert('成功', '引用リポストを投稿しました');
      fetchData();
    } catch (error) {
      console.error('Failed to send quote:', error);
      Alert.alert('エラー', '引用リポストの投稿に失敗しました');
    } finally {
      setIsQuoteSending(false);
    }
  };

  const handleShare = async (post: any, event: any) => {
    event.stopPropagation();
    try {
      const url = `https://bsky.app/profile/${post.author.handle}/post/${post.uri.split('/').pop()}`;
      await Share.share({ message: url });
    } catch (error) {
      console.error('Share failed:', error);
    }
  };

  const renderExternal = (embed: any) => {
    const external = embed?.external || embed?.media?.external;
    if (!external) return null;

    const { uri, title, description, thumb } = external;

    return (
      <TouchableOpacity 
        style={styles.externalBox}
        onPress={() => Linking.openURL(uri)}
        activeOpacity={0.8}
      >
        {thumb && (
          <Image 
            source={{ uri: thumb }} 
            style={styles.externalThumb as any} 
            contentFit="cover"
          />
        )}
        <View style={styles.externalContent}>
          <Text style={styles.externalTitle} numberOfLines={2}>{title || uri}</Text>
          {description ? (
            <Text style={styles.externalDescription} numberOfLines={2}>{description}</Text>
          ) : null}
          <Text style={styles.externalUri} numberOfLines={1}>
            {uri.split('/')[2] || uri}
          </Text>
        </View>
      </TouchableOpacity>
    );
  };

  const renderPost = ({ item }: { item: any }) => {
    const post = item.post;
    const reason = item.reason;
    const isRepost = reason?.$type === 'app.bsky.feed.defs#reasonRepost';
    
    // Handle quote posts and reposts
    let displayPost = post;
    let quotedPost = null;
    
    if (post.embed?.$type === 'app.bsky.embed.record#view') {
      quotedPost = post.embed.record;
    } else if (post.embed?.$type === 'app.bsky.embed.recordWithMedia#view') {
      quotedPost = post.embed.record?.record;
    }

    const author = displayPost.author;
    const hasImages = (displayPost.embed?.images && displayPost.embed.images.length > 0) || 
                      (displayPost.embed?.media?.images && displayPost.embed.media.images.length > 0);
    const images = displayPost.embed?.images || displayPost.embed?.media?.images || [];
    const imageCount = images.length;

    const videoEmbed = displayPost.embed?.$type === 'app.bsky.embed.video#view'
      ? displayPost.embed
      : (displayPost.embed?.$type === 'app.bsky.embed.recordWithMedia#view' && displayPost.embed.media?.$type === 'app.bsky.embed.video#view'
        ? displayPost.embed.media
        : null);

    const quotedVideoEmbed = quotedPost?.embed?.$type === 'app.bsky.embed.video#view'
      ? quotedPost.embed
      : (quotedPost?.embed?.$type === 'app.bsky.embed.recordWithMedia#view' && quotedPost.embed.media?.$type === 'app.bsky.embed.video#view'
        ? quotedPost.embed.media
        : null);

    return (
      <View>
        {/* Repost Header */}
        {isRepost && (
          <View style={styles.repostHeader}>
            <Ionicons name="repeat" size={16} color={theme.colors.textMuted} style={{ marginLeft: 52 }} />
            <Text style={styles.repostText}>
              {reason.by.displayName || reason.by.handle}がリポストしました
            </Text>
          </View>
        )}

        <TouchableOpacity 
          style={styles.postItem} 
          onPress={() => onSelectPost && onSelectPost(displayPost)}
          activeOpacity={0.7}
        >
          {/* Post Header with Author Info */}
          <View style={styles.postHeader}>
            <Image 
              source={{ uri: author?.avatar || 'https://via.placeholder.com/40' }} 
              style={styles.postAvatar as any} 
            />
            <View style={styles.postAuthorInfo}>
              <View style={styles.postAuthorNameRow}>
                <Text style={styles.postAuthorName} numberOfLines={1}>
                  {author?.displayName || author?.handle}
                </Text>
                <Text style={styles.postTime}>· {formatPostTime(displayPost.indexedAt)}</Text>
              </View>
              <Text style={styles.postAuthorHandle} numberOfLines={1}>@{author?.handle}</Text>
            </View>
          </View>

          {/* Post Content */}
          <View style={styles.postContent}>
            {displayPost.record?.text && (
              <Hyperlink 
                linkDefault={true} 
                linkStyle={{ color: theme.colors.primary, textDecorationLine: 'underline' }} 
                style={styles.postText}
                onHashtagPress={onSearchHashtag}
                onHandlePress={onSearchHandle}
              >
                {displayPost.record.text}
              </Hyperlink>
            )}
            
            {/* Images Grid */}
            {hasImages && (
              <View style={styles.postImagesGrid}>
                {images.slice(0, 4).map((img: any, i: number) => {
                  let imageStyle: any[] = [styles.postImage];
                  
                  if (imageCount === 1) {
                    imageStyle.push(styles.postImageSingle);
                  } else if (imageCount === 2) {
                    imageStyle.push(styles.postImageDouble);
                  } else if (imageCount === 3) {
                    if (i === 0) {
                      imageStyle.push(styles.postImageTripleFirst);
                    } else {
                      imageStyle.push(styles.postImageTripleOther);
                    }
                  } else {
                    imageStyle.push(styles.postImageQuad);
                  }

                  return (
                    <View key={i} style={imageStyle}>
                      <Image 
                        source={{ uri: img.thumb }} 
                        style={styles.postImageFull as any}
                        contentFit="cover"
                      />
                    </View>
                  );
                })}
              </View>
            )}

            {/* Video / GIF Embed */}
            {videoEmbed?.playlist && (
              <View style={styles.postVideoWrapper}>
                <VideoEmbed uri={videoEmbed.playlist} aspectRatio={videoEmbed.aspectRatio} />
              </View>
            )}

            {/* External / Link Cards */}
            {displayPost.embed?.$type === 'app.bsky.embed.external#view' && renderExternal(displayPost.embed)}
            {displayPost.embed?.$type === 'app.bsky.embed.recordWithMedia#view' && 
             displayPost.embed.media?.$type === 'app.bsky.embed.external#view' && 
             renderExternal(displayPost.embed.media)}

            {/* Quoted Post */}
            {quotedPost && (
              <TouchableOpacity 
                style={styles.quotedPost}
                onPress={(e) => {
                  e.stopPropagation();
                  onSelectPost && onSelectPost(quotedPost);
                }}
              >
                <View style={styles.quotedPostHeader}>
                  <Image 
                    source={{ uri: quotedPost.author?.avatar || 'https://via.placeholder.com/20' }} 
                    style={styles.quotedPostAvatar as any} 
                  />
                  <Text style={styles.quotedPostAuthor} numberOfLines={1}>
                    {quotedPost.author?.displayName || quotedPost.author?.handle}
                  </Text>
                  <Text style={styles.quotedPostHandle} numberOfLines={1}>
                    @{quotedPost.author?.handle}
                  </Text>
                </View>
                {quotedPost.value?.text && (
                  <Hyperlink 
                    linkDefault={true} 
                    linkStyle={{ color: theme.colors.primary, textDecorationLine: 'underline' }} 
                    style={styles.quotedPostText}
                    onHashtagPress={onSearchHashtag}
                    onHandlePress={onSearchHandle}
                  >
                    {quotedPost.value.text}
                  </Hyperlink>
                )}
                {/* Quoted Post Images */}
                {((quotedPost.embeds?.[0]?.images && quotedPost.embeds[0].images.length > 0) || 
                  (quotedPost.embed?.images && quotedPost.embed.images.length > 0)) && (
                  <View style={styles.quotedPostImages}>
                    {(quotedPost.embeds?.[0]?.images || quotedPost.embed?.images).slice(0, 2).map((img: any, i: number) => (
                      <Image 
                        key={i}
                        source={{ uri: img.thumb }} 
                        style={[styles.quotedPostImage, (quotedPost.embeds?.[0]?.images || quotedPost.embed?.images).length === 1 && styles.quotedPostImageSingle] as any}
                        contentFit="cover"
                      />
                    ))}
                  </View>
                )}
                {/* Quoted Post Video Preview */}
                {quotedVideoEmbed?.playlist && (
                  <View style={styles.quotedPostVideoPreview}>
                    <VideoEmbed uri={quotedVideoEmbed.playlist} aspectRatio={quotedVideoEmbed.aspectRatio} style={styles.quotedPostVideoPreviewInner} />
                  </View>
                )}
              </TouchableOpacity>
            )}
          </View>

          {/* Post Footer with Interaction Buttons */}
          <View style={styles.postFooter}>
            <View style={styles.postInteractions}>
              <TouchableOpacity 
                style={styles.postInteractionButton}
                onPress={(e) => {
                  e.stopPropagation();
                  onSelectPost && onSelectPost(displayPost);
                }}
              >
                <Ionicons name="chatbubble-outline" size={18} color={theme.colors.textMuted} />
                <Text style={styles.postInteractionText}>{displayPost.replyCount || 0}</Text>
              </TouchableOpacity>
              
              <TouchableOpacity 
                style={styles.postInteractionButton}
                onPress={(e) => handleRepost(displayPost, e)}
              >
                <Ionicons 
                  name={displayPost.viewer?.repost ? "repeat" : "repeat-outline"} 
                  size={20} 
                  color={displayPost.viewer?.repost ? theme.colors.success : theme.colors.textMuted} 
                />
                <Text style={[styles.postInteractionText, displayPost.viewer?.repost && { color: theme.colors.success }]}>
                  {displayPost.repostCount || 0}
                </Text>
              </TouchableOpacity>
              
              <TouchableOpacity 
                style={styles.postInteractionButton}
                onPress={(e) => handleLike(displayPost, e)}
              >
                <Ionicons 
                  name={displayPost.viewer?.like ? "heart" : "heart-outline"} 
                  size={18} 
                  color={displayPost.viewer?.like ? theme.colors.danger : theme.colors.textMuted} 
                />
                <Text style={[styles.postInteractionText, displayPost.viewer?.like && { color: theme.colors.danger }]}>
                  {displayPost.likeCount || 0}
                </Text>
              </TouchableOpacity>

              <TouchableOpacity 
                style={styles.postInteractionButton}
                onPress={(e) => handleShare(displayPost, e)}
              >
                <Ionicons name="share-outline" size={18} color={theme.colors.textMuted} />
              </TouchableOpacity>
            </View>
          </View>
        </TouchableOpacity>
      </View>
    );
  };

  return (
    <View style={styles.container}>
      <FlatList
        data={feed}
        keyExtractor={(item, index) => `${item.post.uri}-${index}`}
        renderItem={renderPost}
        ListHeaderComponent={renderHeader}
        refreshing={refreshing}
        onRefresh={() => fetchData(true)}
        onEndReached={() => fetchData(false)}
        onEndReachedThreshold={0.5}
        ListFooterComponent={loadingMore ? (
          <ActivityIndicator style={{ marginVertical: 20 }} color={theme.colors.primary} />
        ) : null}
        contentContainerStyle={styles.listContent}
      />

      {/* Edit Profile Modal */}
      <Modal
        visible={isEditModalVisible}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setIsEditModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <TouchableOpacity onPress={() => setIsEditModalVisible(false)}>
                <Text style={styles.modalCancel}>キャンセル</Text>
              </TouchableOpacity>
              <Text style={styles.modalTitle}>プロフィールを編集</Text>
              <TouchableOpacity onPress={handleSaveProfile} disabled={isSaving}>
                {isSaving ? (
                  <ActivityIndicator size="small" color={theme.colors.primary} />
                ) : (
                  <Text style={styles.modalSave}>保存</Text>
                )}
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.modalContent}>
              {/* Avatar Selection */}
              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>プロフィール画像</Text>
                <TouchableOpacity style={styles.imagePickerButton} onPress={handlePickAvatar}>
                  {editAvatarUri ? (
                    <Image
                      source={{ uri: editAvatarUri }}
                      style={styles.imagePreview as any}
                      contentFit="cover"
                    />
                  ) : (
                    <View style={styles.imagePlaceholder}>
                      <Ionicons name="camera-outline" size={32} color={theme.colors.textMuted} />
                      <Text style={styles.imagePlaceholderText}>画像を選択</Text>
                    </View>
                  )}
                </TouchableOpacity>
              </View>

              {/* Banner Selection */}
              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>カバー画像</Text>
                <TouchableOpacity style={styles.bannerPickerButton} onPress={handlePickBanner}>
                  {editBannerUri ? (
                    <Image
                      source={{ uri: editBannerUri }}
                      style={styles.bannerPreview as any}
                      contentFit="cover"
                    />
                  ) : (
                    <View style={styles.bannerPlaceholder}>
                      <Ionicons name="image-outline" size={32} color={theme.colors.textMuted} />
                      <Text style={styles.imagePlaceholderText}>画像を選択</Text>
                    </View>
                  )}
                </TouchableOpacity>
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>表示名</Text>
                <TextInput
                  style={styles.input}
                  value={editDisplayName}
                  onChangeText={setEditDisplayName}
                  placeholder="表示名を入力"
                  placeholderTextColor={theme.colors.textMuted}
                  maxLength={64}
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>自己紹介</Text>
                <TextInput
                  style={[styles.input, styles.textArea]}
                  value={editDescription}
                  onChangeText={setEditDescription}
                  placeholder="自己紹介を入力"
                  placeholderTextColor={theme.colors.textMuted}
                  multiline
                  numberOfLines={4}
                  maxLength={256}
                  textAlignVertical="top"
                />
              </View>
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* User Menu Modal */}
      <Modal
        visible={isMenuVisible}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setIsMenuVisible(false)}
      >
        <View style={styles.menuModalOverlay}>
          <TouchableOpacity 
            style={{ flex: 1 }} 
            activeOpacity={1} 
            onPress={() => setIsMenuVisible(false)}
          />
          <View style={styles.menuModalContainer}>
            <TouchableOpacity style={styles.menuItem} onPress={handleToggleFavorite}>
              <Ionicons 
                name={isFavorited ? "star" : "star-outline"} 
                size={24} 
                color={isFavorited ? theme.colors.primary : theme.colors.text} 
              />
              <Text style={[styles.menuItemText, isFavorited && { color: theme.colors.primary }]}>
                {isFavorited ? t('profile.removeFromFavorites') : t('profile.addToFavorites')}
              </Text>
            </TouchableOpacity>

            <TouchableOpacity style={styles.menuItem} onPress={handleMute}>
              <Ionicons name="volume-mute-outline" size={24} color={theme.colors.text} />
              <Text style={styles.menuItemText}>{t('profile.mute')}</Text>
            </TouchableOpacity>

            <TouchableOpacity style={styles.menuItem} onPress={handleBlock}>
              <Ionicons name="ban-outline" size={24} color="#FF3B30" />
              <Text style={[styles.menuItemText, styles.menuItemDanger]}>{t('profile.block')}</Text>
            </TouchableOpacity>

            <TouchableOpacity 
              style={styles.menuItem} 
              onPress={() => setIsMenuVisible(false)}
            >
              <Ionicons name="close-outline" size={24} color={theme.colors.textMuted} />
              <Text style={[styles.menuItemText, { color: theme.colors.textMuted }]}>
                {t('common.cancel')}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Repost Menu Modal */}
      <Modal
        visible={repostMenuVisible}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setRepostMenuVisible(false)}
      >
        <View style={styles.menuModalOverlay}>
          <TouchableOpacity 
            style={{ flex: 1 }} 
            activeOpacity={1} 
            onPress={() => setRepostMenuVisible(false)}
          />
          <View style={styles.menuModalContainer}>
            <TouchableOpacity 
              style={styles.menuItem} 
              onPress={() => selectedPostForRepost && executeRepost(selectedPostForRepost)}
            >
              <Ionicons 
                name={selectedPostForRepost?.viewer?.repost ? "repeat" : "repeat-outline"} 
                size={24} 
                color={selectedPostForRepost?.viewer?.repost ? theme.colors.success : theme.colors.text} 
              />
              <Text style={[styles.menuItemText, selectedPostForRepost?.viewer?.repost && { color: theme.colors.success }]}>
                {selectedPostForRepost?.viewer?.repost ? 'リポストを取り消す' : 'リポスト'}
              </Text>
            </TouchableOpacity>

            <TouchableOpacity 
              style={styles.menuItem} 
              onPress={() => selectedPostForRepost && executeQuote(selectedPostForRepost)}
            >
              <Ionicons name="chatbubble-outline" size={24} color={theme.colors.text} />
              <Text style={styles.menuItemText}>引用リポスト</Text>
            </TouchableOpacity>

            <TouchableOpacity 
              style={styles.menuItem} 
              onPress={() => setRepostMenuVisible(false)}
            >
              <Ionicons name="close-outline" size={24} color={theme.colors.textMuted} />
              <Text style={[styles.menuItemText, { color: theme.colors.textMuted }]}>
                {t('common.cancel')}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Quote Modal */}
      <Modal
        visible={quoteModalVisible}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setQuoteModalVisible(false)}
      >
        <KeyboardAvoidingView 
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          style={styles.quoteModalContainer}
        >
          <View style={styles.quoteModalContent}>
            <View style={styles.quoteModalHeader}>
              <TouchableOpacity onPress={() => setQuoteModalVisible(false)}>
                <Text style={styles.quoteCancelText}>キャンセル</Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={[styles.quoteSendButton, !quoteText.trim() && styles.quoteSendButtonDisabled]}
                onPress={handleSendQuote}
                disabled={!quoteText.trim() || isQuoteSending}
              >
                {isQuoteSending ? (
                  <ActivityIndicator size="small" color={theme.colors.primaryText} />
                ) : (
                  <Text style={styles.quoteSendText}>投稿</Text>
                )}
              </TouchableOpacity>
            </View>
            
            {selectedPostForRepost && (
              <View style={styles.quoteTargetPreview}>
                <Image source={{ uri: selectedPostForRepost.author?.avatar }} style={styles.quoteTargetAvatar as any} />
                <View style={styles.quoteTargetInfo}>
                  <Text style={styles.quoteTargetAuthor}>@{selectedPostForRepost.author?.handle} を引用</Text>
                  <Text style={styles.quoteTargetText} numberOfLines={2}>
                    {extractPlainText(selectedPostForRepost)}
                  </Text>
                </View>
              </View>
            )}

            <TextInput
              style={styles.quoteInput}
              placeholder="コメントを追加"
              placeholderTextColor={theme.colors.textMuted}
              multiline
              autoFocus
              value={quoteText}
              onChangeText={setQuoteText}
              maxLength={300}
            />
          </View>
        </KeyboardAvoidingView>
      </Modal>
    </View>
  );
};

const createStyles = (theme: Theme) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.background,
  },
  coverContainer: {
    width: SCREEN_WIDTH,
    height: 200,
    backgroundColor: theme.colors.cardSecondary,
  },
  coverImage: {
    width: '100%',
    height: '100%',
    backgroundColor: theme.colors.cardSecondary,
  },
  backButton: {
    position: 'absolute',
    left: 16,
    backgroundColor: theme.colors.overlay,
    borderRadius: 20,
    padding: 4,
  },
  editButton: {
    position: 'absolute',
    right: 16,
    backgroundColor: theme.colors.overlay,
    borderRadius: 20,
    padding: 4,
  },
  profileInfoContainer: {
    alignItems: 'center',
    marginTop: -50,
    paddingHorizontal: 20,
    paddingBottom: 20,
    borderBottomWidth: 8,
    borderBottomColor: theme.colors.border,
    backgroundColor: theme.colors.card,
  },
  avatar: {
    width: 100,
    height: 100,
    borderRadius: 50,
    borderWidth: 4,
    borderColor: theme.colors.card,
    backgroundColor: theme.colors.cardSecondary,
  },
  displayName: {
    fontSize: 22,
    fontWeight: 'bold',
    marginTop: 10,
    color: theme.colors.text,
  },
  handle: {
    fontSize: 14,
    color: theme.colors.textMuted,
    marginTop: 2,
  },
  description: {
    fontSize: 14,
    color: theme.colors.text,
    textAlign: 'center',
    marginTop: 12,
    lineHeight: 20,
  },
  statsContainer: {
    flexDirection: 'row',
    marginTop: 20,
    width: '100%',
    justifyContent: 'space-evenly',
    paddingHorizontal: 10,
  },
  statItem: {
    alignItems: 'center',
    flex: 1,
  },
  statNumber: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.text,
  },
  statLabel: {
    fontSize: 12,
    color: theme.colors.textMuted,
    marginTop: 2,
  },
  actionButtons: {
    flexDirection: 'row',
    marginTop: 25,
    width: '100%',
    justifyContent: 'center',
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.cardSecondary,
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 25,
    marginHorizontal: 8,
  },
  followButton: {
    backgroundColor: theme.colors.primary,
  },
  unfollowButton: {
    backgroundColor: theme.colors.textMuted,
  },
  actionButtonText: {
    color: theme.colors.primaryText,
    fontWeight: 'bold',
    marginLeft: 8,
  },
  sectionHeader: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: theme.colors.card,
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.border,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: theme.colors.text,
  },
  listContent: {
    paddingBottom: 40,
  },
  repostHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 8,
    paddingHorizontal: 16,
    backgroundColor: theme.colors.card,
  },
  repostText: {
    fontSize: 13,
    color: theme.colors.textMuted,
    marginLeft: 6,
  },
  quotedPost: {
    marginTop: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: theme.colors.border,
    borderRadius: 8,
    backgroundColor: theme.colors.cardSecondary,
  },
  quotedPostHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  quotedPostAvatar: {
    width: 20,
    height: 20,
    borderRadius: 10,
    marginRight: 6,
    backgroundColor: theme.colors.background,
  },
  quotedPostAuthor: {
    fontSize: 13,
    fontWeight: '600',
    color: theme.colors.text,
    marginRight: 4,
    maxWidth: 120,
  },
  quotedPostHandle: {
    fontSize: 13,
    color: theme.colors.textMuted,
    flex: 1,
  },
  quotedPostText: {
    fontSize: 14,
    color: theme.colors.text,
    lineHeight: 19,
  },
  quotedPostImages: {
    flexDirection: 'row',
    marginTop: 8,
    gap: 4,
  },
  quotedPostImage: {
    width: 80,
    height: 80,
    borderRadius: 8,
    backgroundColor: theme.colors.cardSecondary,
  },
  quotedPostImageSingle: {
    width: '100%',
    height: 120,
  },
  postItem: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
    backgroundColor: theme.colors.card,
  },
  postHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  postAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: theme.colors.cardSecondary,
    marginRight: 12,
  },
  postAuthorInfo: {
    flex: 1,
    justifyContent: 'center',
  },
  postAuthorNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  postAuthorName: {
    fontSize: 15,
    fontWeight: '600',
    color: theme.colors.text,
    marginRight: 6,
  },
  postAuthorHandle: {
    fontSize: 13,
    color: theme.colors.textMuted,
    marginTop: 1,
  },
  postContent: {
    marginLeft: 52,
  },
  postText: {
    fontSize: 15,
    color: theme.colors.text,
    lineHeight: 21,
    marginBottom: 8,
  },
  postImagesGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 8,
    marginBottom: 8,
    gap: 2,
  },
  postImage: {
    backgroundColor: theme.colors.cardSecondary,
    borderRadius: 12,
    overflow: 'hidden',
  },
  postImageSingle: {
    width: '100%',
    height: 280,
  },
  postImageDouble: {
    width: '49.5%',
    height: 200,
  },
  postImageTripleFirst: {
    width: '100%',
    height: 200,
  },
  postImageTripleOther: {
    width: '49.5%',
    height: 130,
  },
  postImageQuad: {
    width: '49.5%',
    height: 150,
  },
  postImageFull: {
    width: '100%',
    height: '100%',
  },
  postVideoWrapper: {
    marginTop: 8,
    marginBottom: 8,
    borderRadius: 12,
    overflow: 'hidden',
  },
  quotedPostVideoPreview: {
    width: 60,
    height: 60,
    borderRadius: 8,
    marginTop: 8,
    overflow: 'hidden',
    backgroundColor: theme.colors.border,
  },
  quotedPostVideoPreviewInner: {
    borderRadius: 0,
  },
  postFooter: {
    marginLeft: 52,
    marginTop: 8,
  },
  postInteractions: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    maxWidth: 400,
  },
  postInteractionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 4,
    paddingHorizontal: 8,
    marginRight: 8,
  },
  postInteractionText: {
    marginLeft: 6,
    color: theme.colors.textMuted,
    fontSize: 13,
    fontWeight: '500',
  },
  postTime: {
    color: theme.colors.textMuted,
    fontSize: 13,
  },
  postStat: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 12,
  },
  postStatText: {
    marginLeft: 4,
    color: theme.colors.textMuted,
    fontSize: 12,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: theme.colors.overlay,
    justifyContent: 'flex-end',
  },
  modalContainer: {
    backgroundColor: theme.colors.card,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    maxHeight: '80%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.border,
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: theme.colors.text,
  },
  modalCancel: {
    fontSize: 16,
    color: theme.colors.textMuted,
  },
  modalSave: {
    fontSize: 16,
    color: theme.colors.primary,
    fontWeight: '600',
  },
  modalContent: {
    padding: 16,
  },
  inputGroup: {
    marginBottom: 20,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.text,
    marginBottom: 8,
  },
  input: {
    backgroundColor: theme.colors.input,
    borderWidth: 1,
    borderColor: theme.colors.border,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    color: theme.colors.text,
  },
  textArea: {
    minHeight: 100,
    paddingTop: 10,
  },
  imagePickerButton: {
    width: 100,
    height: 100,
    borderRadius: 50,
    overflow: 'hidden',
    backgroundColor: theme.colors.cardSecondary,
    alignSelf: 'center',
  },
  imagePreview: {
    width: '100%',
    height: '100%',
  },
  imagePlaceholder: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  imagePlaceholderText: {
    marginTop: 4,
    fontSize: 12,
    color: theme.colors.textMuted,
  },
  bannerPickerButton: {
    width: '100%',
    height: 120,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: theme.colors.cardSecondary,
  },
  bannerPreview: {
    width: '100%',
    height: '100%',
  },
  bannerPlaceholder: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  menuButton: {
    position: 'absolute',
    right: 12,
    zIndex: 10,
    backgroundColor: 'rgba(0,0,0,0.3)',
    borderRadius: 20,
    padding: 8,
  },
  externalBox: {
    marginTop: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.border,
    overflow: 'hidden',
    backgroundColor: theme.colors.card,
  },
  externalThumb: {
    width: '100%',
    aspectRatio: 1.91,
    backgroundColor: theme.colors.cardSecondary,
  },
  externalContent: {
    padding: 10,
  },
  externalTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: theme.colors.text,
    marginBottom: 4,
  },
  externalDescription: {
    fontSize: 13,
    color: theme.colors.textMuted,
    marginBottom: 6,
  },
  externalUri: {
    fontSize: 12,
    color: theme.colors.primary,
  },
  menuModalOverlay: {
    flex: 1,
    backgroundColor: theme.colors.overlay,
    justifyContent: 'flex-end',
  },
  menuModalContainer: {
    backgroundColor: theme.colors.card,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    paddingBottom: 20,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 16,
    paddingHorizontal: 20,
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.border,
  },
  menuItemText: {
    marginLeft: 12,
    fontSize: 16,
    color: theme.colors.text,
  },
  menuItemDanger: {
    color: '#FF3B30',
  },
  quoteModalContainer: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end',
  },
  quoteModalContent: {
    backgroundColor: theme.colors.card,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    height: '90%',
    padding: 15,
  },
  quoteModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  quoteCancelText: {
    fontSize: 16,
    color: theme.colors.text,
  },
  quoteSendButton: {
    backgroundColor: theme.colors.primary,
    paddingHorizontal: 20,
    paddingVertical: 8,
    borderRadius: 20,
  },
  quoteSendButtonDisabled: {
    opacity: 0.6,
  },
  quoteSendText: {
    color: theme.colors.primaryText,
    fontWeight: 'bold',
    fontSize: 16,
  },
  quoteTargetPreview: {
    flexDirection: 'row',
    marginBottom: 15,
    padding: 10,
    backgroundColor: theme.colors.cardSecondary,
    borderRadius: 12,
  },
  quoteTargetAvatar: {
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: theme.colors.cardSecondary,
  },
  quoteTargetInfo: {
    flex: 1,
    marginLeft: 10,
  },
  quoteTargetAuthor: {
    fontSize: 12,
    color: theme.colors.textMuted,
    marginBottom: 2,
  },
  quoteTargetText: {
    fontSize: 14,
    color: theme.colors.text,
  },
  quoteInput: {
    flex: 1,
    fontSize: 18,
    textAlignVertical: 'top',
    color: theme.colors.text,
  },
});

export default Profile;
